import { useMemo, useState } from "react";
import { Search, AlertTriangle, Star, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AvailBadge, Avatar } from "@/components/atlas/Shared";
import { usePositions } from "@/lib/positions";
import { useLang } from "@/lib/i18n";

const AVAIL_RANK = { available: 0, maybe: 1, undefined: 2, null: 2, unavailable: 3 };

const CandidateRow = ({ c, slot, onAssign, disabled }) => {
  const { t } = useLang();
  const { label } = usePositions();
  const qualified = slot ? c.positions.includes(slot.position_key) : true;
  const interested = slot && c.interested_slots.includes(slot.id);
  const inEvent = c.assigned_slot_ids.length > 0;
  const inSlot = !!slot && c.assigned_slot_ids.includes(slot.id);
  return (
    <div
      draggable={!disabled}
      onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify({ type: "candidate", id: c.id }))}
      data-testid={`candidate-${c.id}`}
      className={`px-4 py-3 border-b border-black/[0.05] ${inSlot ? "opacity-45" : "cursor-grab hover:bg-[#C5A059]/[0.05]"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-center gap-2.5">
          <Avatar name={c.name} size={30} />
          <div className="min-w-0">
          <div className="text-sm font-medium truncate flex items-center gap-1.5">
            {c.name}
            {interested && <Star className="w-3 h-3 fill-[#C5A059] text-[#C5A059]" aria-label={t("b.interested")} />}
          </div>
          <div className="text-[11px] text-neutral-500 truncate">{label(c.primary_position)}{c.positions.length > 1 ? ` +${c.positions.length - 1}` : ""}</div>
          </div>
        </div>
        <AvailBadge status={c.availability} testid={`candidate-avail-${c.id}`} />
      </div>
      <div className="flex items-center justify-between gap-2 mt-2">
        <div className="flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wider">
          {inEvent && <span className="text-neutral-500" data-testid={`candidate-in-event-${c.id}`}>{inSlot ? t("b.inSlot") : `${t("b.inEvent")} (${c.assigned_slot_ids.length})`}</span>}
          {c.conflicts.length > 0 && <span className="flex items-center gap-1 text-red-600" title={c.conflicts.join(", ")} data-testid={`candidate-conflict-${c.id}`}><AlertTriangle className="w-3 h-3" />{t("b.conflict")}</span>}
          {slot && !qualified && <span className="text-amber-700">{t("b.notQualified")}</span>}
          {c.phone && <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-neutral-400 hover:text-black normal-case tracking-normal"><Phone className="w-3 h-3" />{c.phone}</a>}
        </div>
        {!inSlot && !disabled && (
          <button data-testid={`assign-btn-${c.id}`} disabled={!slot} onClick={() => onAssign(c)}
            className="press shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#C5A059] text-[#0A0A0C] hover:bg-[#D9B872] disabled:opacity-25">
            {t("b.assign")}
          </button>
        )}
      </div>
    </div>
  );
};

export const CandidatePool = ({ candidates, slot, onAssign, disabled }) => {
  const { t } = useLang();
  const { label } = usePositions();
  const [q, setQ] = useState("");
  const [qualifiedOnly, setQualifiedOnly] = useState(true);
  const [availOnly, setAvailOnly] = useState(false);

  const list = useMemo(() => {
    let l = candidates.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
    if (slot && qualifiedOnly) l = l.filter((c) => c.positions.includes(slot.position_key));
    if (availOnly) l = l.filter((c) => c.availability === "available" || c.availability === "maybe");
    const score = (c) => (slot && c.assigned_slot_ids.includes(slot.id) ? 100 : c.assigned_slot_ids.length ? 20 : 0) + (c.conflicts.length ? 50 : 0) + AVAIL_RANK[c.availability] * 10 - (slot && c.interested.includes(slot.position_key) ? 5 : 0);
    return [...l].sort((a, b) => score(a) - score(b) || a.name.localeCompare(b.name));
  }, [candidates, q, slot, qualifiedOnly, availOnly]);

  return (
    <div className="card-lux flex flex-col max-h-[calc(100vh-140px)]" data-testid="candidate-pool">
      <div className="p-4 border-b border-black/[0.06] space-y-3">
        <div className="flex items-baseline justify-between">
          <h3 className="font-display text-2xl">{t("b.pool")}</h3>
          <span className="mono text-xs text-neutral-400" data-testid="candidate-count">{list.length}</span>
        </div>
        <div className="text-xs px-3 py-2 rounded-lg bg-[#C5A059]/[0.08] border border-[#C5A059]/30" data-testid="selected-slot-label">
          <span className="eyebrow mr-2">{t("b.selected")}</span>{slot ? label(slot.position_key) : t("b.selectSlot")}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-neutral-400" />
          <Input data-testid="candidate-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("c.search")} className="pl-9 h-9 rounded-none" />
        </div>
        <div className="flex flex-wrap gap-4 text-xs">
          <label className="flex items-center gap-2"><Switch data-testid="filter-qualified-switch" checked={qualifiedOnly} onCheckedChange={setQualifiedOnly} />{t("b.qualifiedOnly")}</label>
          <label className="flex items-center gap-2"><Switch data-testid="filter-available-switch" checked={availOnly} onCheckedChange={setAvailOnly} />{t("b.availOnly")}</label>
        </div>
        <p className="text-[11px] text-neutral-400 leading-relaxed">{t("b.poolHint")}</p>
      </div>
      <div className="overflow-y-auto flex-1">
        {list.map((c) => <CandidateRow key={c.id} c={c} slot={slot} onAssign={onAssign} disabled={disabled} />)}
        {list.length === 0 && <p className="p-6 text-sm text-neutral-400 text-center">{t("c.none")}</p>}
      </div>
    </div>
  );
};
